import duration from 'dayjs/plugin/duration';
import styled from 'styled-components';
import React, { Dispatch, FC, useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { push as Push } from 'redux-first-history';
import { CampaignTemplateStats, CampaignType, TemplateLink } from '@dev/ee-api/ee-api-models';
import { filter, isEmpty, map, truncate } from 'lodash-es';
import {
    ContentLoader,
    CustomTable,
    CustomTableHeadersProps,
    CustomTableRowsProps,
    EmptyState,
    MobileCardRowsProps,
    MobileTable,
    palette,
    Paper,
    StackedBarChart,
    TertiaryButton,
    Typography,
} from '@dev/ee-components';
import { safelyDecodeBase64Url } from '@dev/ee-tools';
import { Box } from '@material-ui/core';
import { DataInterface as StackedBarChartDataInterface } from '@dev/ee-components/src/components/charts/StackedBarChart/StackedBarChartProps';

import dayjs from '@/services/tools/dayjs';
import { ApplicationState } from '@/store';
import { CancelPollingCampaignSimpleStats, GetCampaignByName } from '@/store/campaigns/actions';
import { PickedCampaignInterface } from '@/store/campaigns/types';
import { BarChartColors, MINUTES_IN_DAY } from '@/services/constants';
import { CampaignsPaths } from '@/services/dictionaries/Paths';
import { StatusBadge } from '@/assets/StatusBadge';

import { CampaignReportAbSplitProps } from './CampaignReportAbSplitInterface';
import { getWinnerTemplate, isNewAbSplit } from '../../helperFunctions';
import {
    GetWinnerResults,
    TestResults,
    TestResultsTable,
    TestResultsTableMobile,
    WinnerResultsMobile,
} from './TestAndWinnerResultsSection';
import { AbSplitClicksAndOpensLineChart } from './LineChartSection';

dayjs.extend(duration);

const LinksTableContainerTitleRow = styled.div`
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
`;

const LinkColumn = styled.div`
    padding: 20px 0;
`;
const LinkColumnMobile = styled.div`
    overflow-wrap: break-word;
    white-space: break-spaces;
`;

const LinkTemplatesSection = styled.div`
    padding-top: 16px;
`;

const LinksVersionWrapper = styled.div`
    text-transform: 'capitalize';
`;
const RecipientsMobile = styled.span`
    font-weight: normal;
    color: ${palette.gray[5]};
    margin-right: 6px;
`;

const newAbSplitName = (singleTemplate: CampaignTemplateStats, templateLabels, winnerCaptalizeTranslation): string =>
    singleTemplate.IsWinner ? winnerCaptalizeTranslation : templateLabels ? templateLabels[singleTemplate?.Name] : null;

const newAbSplitBackground = (singleTemplate: CampaignTemplateStats, index: number): string =>
    singleTemplate?.IsWinner ? palette.jacarta[1] : BarChartColors[index].bgColor;

const barChartFontColor = (singleTemplate: CampaignTemplateStats, index: number): string =>
    singleTemplate.IsWinner ? palette.jacarta[5] : BarChartColors[index].txtColor;

const barChartBadge = (singleTemplate: CampaignTemplateStats, winnerCaptalizeTranslation: string): JSX.Element => {
    if (singleTemplate.IsWinnerTemplate) {
        return <StatusBadge status="Completed" value={winnerCaptalizeTranslation} />;
    }
    return null;
};

const barChartIsWinnerCell = (isNewAb, singleTemplate: CampaignTemplateStats): boolean =>
    isNewAb ? singleTemplate.IsWinner : null;

const barChartBackground = (isNewAb, newAbBackground: string, index: number): string =>
    isNewAb ? newAbBackground : BarChartColors[index].bgColor;

const useBarChartData = (): StackedBarChartDataInterface[] => {
    const { t } = useTranslation();
    const { simpleStatsAllData, pickedCampaign } = useSelector((state: ApplicationState) => state.campaigns);
    const { templateLabels, campaign } = pickedCampaign;

    if (isEmpty(pickedCampaign?.simpleStats)) {
        return [];
    }

    return map(simpleStatsAllData?.CampaignTemplatesSummary, (singleTemplate, index) => {
        const simpleStatsLength = simpleStatsAllData?.CampaignTemplatesSummary?.length || 0;
        const isNewAb = isNewAbSplit(simpleStatsAllData?.CampaignTemplatesSummary);
        const newAbName = newAbSplitName(singleTemplate, templateLabels, t('winnerCaptalize'));
        const newAbBackground = newAbSplitBackground(singleTemplate, index);

        let name = templateLabels[singleTemplate?.Name];

        if (isNewAb) {
            name = newAbName;

            if (simpleStatsLength >= 7) {
                name = simpleStatsLength - 1 === index ? newAbName : '';
            }
        }

        return {
            count: singleTemplate.Stats.Recipients || 0,
            percentage: (singleTemplate.Stats.Recipients / campaign.Stats.Recipients) * 100 || 0,
            background: barChartBackground(isNewAb, newAbBackground, index),
            badge: barChartBadge(singleTemplate, t('winnerCaptalize')),
            name,
            fontColor: barChartFontColor(singleTemplate, index),
            isWinnerCell: barChartIsWinnerCell(isNewAb, singleTemplate),
        };
    });
};

const useTableNameData = (): string[] => {
    const { t } = useTranslation();
    const { simpleStatsAllData, pickedCampaign } = useSelector((state: ApplicationState) => state.campaigns);
    const { templateLabels } = pickedCampaign;

    if (isEmpty(pickedCampaign?.simpleStats)) {
        return [];
    }

    return map(simpleStatsAllData?.CampaignTemplatesSummary, (singleTemplate, index) => {
        const simpleStatsLength = simpleStatsAllData?.CampaignTemplatesSummary?.length || 0;
        const isNewAb = isNewAbSplit(simpleStatsAllData?.CampaignTemplatesSummary);
        const newAbName = newAbSplitName(singleTemplate, templateLabels, t('winnerCaptalize'));

        let name = templateLabels[singleTemplate?.Name];

        if (isNewAb) {
            name = newAbName;

            if (simpleStatsLength >= 7) {
                name = simpleStatsLength - 1 === index ? newAbName : '';
            }
        }

        return barChartIsWinnerCell(isNewAb, singleTemplate) ? t('winnerCaptalize') : name;
    });
};

const useTableData = (): MobileCardRowsProps[] => {
    const { simpleStatsAllData, pickedCampaign } = useSelector((state: ApplicationState) => state.campaigns);
    const { campaign } = pickedCampaign;

    if (isEmpty(pickedCampaign?.simpleStats)) {
        return [];
    }
    const arrayOfData = [];
    const data = map(simpleStatsAllData?.CampaignTemplatesSummary, (singleTemplate) => (
        <div>
            <RecipientsMobile>({singleTemplate.Stats.Recipients || 0})</RecipientsMobile>{' '}
            {(singleTemplate.Stats.Recipients / campaign.Stats.Recipients) * 100 || 0}%
        </div>
    ));
    arrayOfData.push(data);

    return arrayOfData;
};

const generateWinnerConditionString = (pickedCampaign: PickedCampaignInterface, t: TFunction): string => {
    const { simpleStats, campaign } = pickedCampaign;

    if (isEmpty(campaign?.CampaignTemplates)) {
        return '';
    }

    const generateHumanizeWinnerAfterTimeLabel = (): string => {
        let humanizeLabelAfterTime;

        if (campaign?.SplitOptions?.DetermineWinnerAfterXMinutes > MINUTES_IN_DAY) {
            humanizeLabelAfterTime = dayjs
                .duration({ minutes: campaign?.SplitOptions?.DetermineWinnerAfterXMinutes })
                .asDays();
            return humanizeLabelAfterTime > 1
                ? `${humanizeLabelAfterTime} ${t('days')}`
                : `${humanizeLabelAfterTime} ${t('day')}`;
        } else {
            humanizeLabelAfterTime = dayjs
                .duration({ minutes: campaign?.SplitOptions?.DetermineWinnerAfterXMinutes })
                .asHours();
            return humanizeLabelAfterTime > 1
                ? `${humanizeLabelAfterTime} ${t('hours')}`
                : `${humanizeLabelAfterTime} ${t('hour')}`;
        }
    };

    const winnerAfterTimeTranslation = (): string =>
        isNewAbSplit(simpleStats?.CampaignTemplatesSummary)
            ? `${t('after')} ${generateHumanizeWinnerAfterTimeLabel()}.`
            : '.';

    const campaignTemplatesLength = isNewAbSplit(simpleStats?.CampaignTemplatesSummary)
        ? campaign.CampaignTemplates.length - 1
        : campaign?.CampaignTemplates?.length;

    return `${t('testing')} ${campaignTemplatesLength} ${t('versionswhichdetermine')} ${
        campaign?.SplitOptions?.SplitOptimization
    } ${winnerAfterTimeTranslation()}`;
};

const TestConditions = (props: { pickedCampaign: PickedCampaignInterface }): JSX.Element => {
    const { t } = useTranslation();

    return (
        <>
            <Typography variant="h1">{t('testconditions')}</Typography>
            <Box mb="32px" />
            <Typography variant="caption">{generateWinnerConditionString(props.pickedCampaign, t)}</Typography>
        </>
    );
};

const BarChart = (props: { pickedCampaign: PickedCampaignInterface }): JSX.Element => {
    const { pickedCampaign } = props;
    const { simpleStats } = pickedCampaign;
    const simpleStatsLength = simpleStats?.CampaignTemplatesSummary?.length || 0;
    const cellPadding = simpleStatsLength >= 7 ? '8px 0px' : '8px 6px';
    const barChartData = useBarChartData();

    if (isEmpty(simpleStats)) {
        return <ContentLoader mode="row" height={40} key="skeleton" />;
    }

    return (
        <StackedBarChart
            barWrapperRadius={simpleStatsLength >= 7 ? '6px' : null}
            data={barChartData}
            totalCount={simpleStatsLength ?? 0}
            cellPadding={cellPadding}
        />
    );
};

const MobileCampaignTable = (props: { pickedCampaign: PickedCampaignInterface }): JSX.Element => {
    const { pickedCampaign } = props;
    const { simpleStats } = pickedCampaign;

    const tableNameData = useTableNameData();
    const tableData = useTableData();

    if (isEmpty(simpleStats)) {
        return <ContentLoader mode="row" height={40} key="skeleton" />;
    }

    return <MobileTable headers={tableNameData} rows={tableData} />;
};

const SeeAllButtonClick = (type: CampaignType, name: string, dispatch: Dispatch<any>) => {
    dispatch(Push(CampaignsPaths.CampaignReportAbSplitLinks.replace(':type', type).replace(':name', name)));
};

const RenderLinksTable = (props: {
    name: string;
    type: CampaignType;
    links: TemplateLink[];
    isNewAbSplitCampaign: boolean;
    templateLabels;
}): JSX.Element => {
    const { isNewAbSplitCampaign, name, type, links, templateLabels } = props;
    const { loading } = useSelector((state: ApplicationState) => state.reports.linkTrackingAbSplit);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const linksHeaders: CustomTableHeadersProps[] = [
        {
            id: 'link',
            label: t('link'),
        },
        {
            id: 'uniqueclicks',
            label: t('uniqueclicks'),
            columnAlign: 'right',
            headerAlign: 'right',
        },
        {
            id: 'clicks',
            label: t('clicks'),
            columnAlign: 'right',
            headerAlign: 'right',
        },
    ];

    const columnsWidth = {
        link: '70%',
        uniqueclicks: '15%',
        clicks: '15%',
    };

    return (
        <Box mt="32px">
            <LinksTableContainerTitleRow>
                <Typography variant="h3">{t('links')}</Typography>
                {links?.length > 3 ? (
                    <TertiaryButton text={t('seeall')} onClick={() => SeeAllButtonClick(type, name, dispatch)} />
                ) : null}
            </LinksTableContainerTitleRow>
            <Paper padding={{ top: 24 }}>
                <CustomTable
                    headers={linksHeaders}
                    rows={GetLinksRows(isNewAbSplitCampaign, templateLabels, links, t)}
                    isEmpty={isEmpty(links) && !loading}
                    loading={loading}
                    stickyHeader={true}
                    rowPadding={48}
                    rowHeight={60}
                    hideCheckboxes={true}
                    columnsWidth={columnsWidth}
                    emptyStateProps={{ text: t('emptystate') }}
                    disableRowClick={true}
                    disableFirstRowBorderTop={true}
                />
            </Paper>
        </Box>
    );
};
const linksHeadersMobile = (
    isNewAbSplitCampaign: boolean,
    templateLabels: { [key: string]: string },
    links: TemplateLink[],
    t
): string[] | JSX.Element[] =>
    map(links?.slice(0, 3), (link) => {
        const linkStatsWithoutWinnerForNewSplit = isNewAbSplitCampaign
            ? filter(link?.Stats, (link) => !link.IsWinner)
            : link?.Stats;

        return (
            (
                <>
                    {t('uniqueclicks')}
                    <LinkTemplatesSection>
                        {map(linkStatsWithoutWinnerForNewSplit, (stat, j) => {
                            const key = templateLabels[stat.Template];
                            return (
                                <Typography key={j} variant="body1" bodyVariant="help">
                                    {t('version')} {key}
                                </Typography>
                            );
                        })}
                    </LinkTemplatesSection>
                </>
            ),
            (
                <>
                    {t('clicks')}
                    <LinkTemplatesSection>
                        {map(linkStatsWithoutWinnerForNewSplit, (stat, i) => {
                            const key = templateLabels[stat.Template];
                            return (
                                <Typography key={i} variant="body1" bodyVariant="help">
                                    {t('version')} {key}
                                </Typography>
                            );
                        })}
                    </LinkTemplatesSection>
                </>
            )
        );
    });

const LinksTableMobile = (props: {
    name: string;
    type: CampaignType;
    links: TemplateLink[];
    isNewAbSplitCampaign: boolean;
    templateLabels;
}): JSX.Element => {
    const { isNewAbSplitCampaign, name, type, links, templateLabels } = props;
    const { loading } = useSelector((state: ApplicationState) => state.reports.linkTrackingAbSplit);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    return (
        <Box mt="32px">
            <LinksTableContainerTitleRow>
                <Typography variant="h3">{t('links')}</Typography>
                {links?.length > 3 ? (
                    <TertiaryButton text={t('seeall')} onClick={() => SeeAllButtonClick(type, name, dispatch)} />
                ) : null}
            </LinksTableContainerTitleRow>
            <MobileTable
                headers={linksHeadersMobile(isNewAbSplitCampaign, templateLabels, links, t)}
                rows={useGetLinksRowsMobile(isNewAbSplitCampaign, templateLabels, links, loading)}
                disableSimplePopover
            />
        </Box>
    );
};

const useGetLinksRowsMobile = (
    isNewAbSplitCampaign: boolean,
    templateLabels: { [key: string]: string },
    links: TemplateLink[],
    loading
): CustomTableRowsProps[] => {
    if (loading) {
        return [
            {
                tableTitle: <ContentLoader count={1} mode="row" />,
                uniqueclicks: <ContentLoader count={1} mode="row" />,
                clicks: <ContentLoader count={1} mode="row" />,
            },
        ];
    }

    if (!loading && (isEmpty(links) || !templateLabels)) {
        return [];
    }

    return map(links?.slice(0, 3), (link) => {
        const linkStatsWithoutWinnerForNewSplit = isNewAbSplitCampaign
            ? filter(link?.Stats, (link) => !link.IsWinner)
            : link?.Stats;
        return {
            tableTitle: (
                <LinkColumnMobile>
                    <Typography variant="body1" bodyVariant="onPaper">
                        {link?.Link}
                    </Typography>
                </LinkColumnMobile>
            ),
            uniqueclicks: (
                <>
                    <Typography variant="body1" bodyVariant="onPaper">
                        {link.Summary.UniqueClicks}
                    </Typography>
                    <LinkTemplatesSection>
                        {map(linkStatsWithoutWinnerForNewSplit, (stat, k) => (
                            <Typography key={k} variant="body1" bodyVariant="onPaper">
                                {stat.UniqueClicks}
                            </Typography>
                        ))}
                    </LinkTemplatesSection>
                </>
            ),
            clicks: (
                <>
                    <Typography variant="body1" bodyVariant="onPaper">
                        {link.Summary.Clicks}
                    </Typography>
                    <LinkTemplatesSection>
                        {map(linkStatsWithoutWinnerForNewSplit, (stat, l) => (
                            <Typography key={l} variant="body1" bodyVariant="onPaper">
                                {stat.Clicks}
                            </Typography>
                        ))}
                    </LinkTemplatesSection>
                </>
            ),
        };
    });
};

const GetLinksRows = (
    isNewAbSplitCampaign: boolean,
    templateLabels: { [key: string]: string },
    links: TemplateLink[],
    t
): CustomTableRowsProps[] => {
    if (isEmpty(links) || !templateLabels) {
        return [];
    }

    return map(links?.slice(0, 3), (link) => {
        const linkStatsWithoutWinnerForNewSplit = isNewAbSplitCampaign
            ? filter(link?.Stats, (link) => !link.IsWinner)
            : link?.Stats;
        return {
            link: (
                <LinkColumn>
                    {truncate(link?.Link, { length: 70 })}
                    <LinkTemplatesSection>
                        {map(linkStatsWithoutWinnerForNewSplit, (stat, i) => {
                            const key = templateLabels[stat.Template];
                            return (
                                <LinksVersionWrapper key={`linksVersionWrapper${i}`}>
                                    <Typography key={i} variant="body1" bodyVariant="help">
                                        {`${t('version')} ${key}`}
                                    </Typography>
                                </LinksVersionWrapper>
                            );
                        })}
                    </LinkTemplatesSection>
                </LinkColumn>
            ),
            uniqueclicks: (
                <>
                    {link.Summary.UniqueClicks}
                    <LinkTemplatesSection>
                        {map(linkStatsWithoutWinnerForNewSplit, (stat, i) => (
                            <Typography key={i} variant="body1" bodyVariant="help">
                                {stat.UniqueClicks}
                            </Typography>
                        ))}
                    </LinkTemplatesSection>
                </>
            ),
            clicks: (
                <>
                    {link.Summary.Clicks}
                    <LinkTemplatesSection>
                        {map(linkStatsWithoutWinnerForNewSplit, (stat, i) => (
                            <Typography key={i} variant="body1" bodyVariant="help">
                                {stat.Clicks}
                            </Typography>
                        ))}
                    </LinkTemplatesSection>
                </>
            ),
        };
    });
};

const CampaignReportAbSplit: FC<CampaignReportAbSplitProps> = (props: CampaignReportAbSplitProps): JSX.Element => {
    const {
        match: { params },
    } = props;
    const decodedName = safelyDecodeBase64Url(params.name);
    const campaignType = params.type;
    const dispatch = useDispatch();
    const { pickedCampaign } = useSelector((state: ApplicationState) => state.campaigns);
    const links = useSelector((state: ApplicationState) => state.reports.linkTrackingAbSplit.collection);
    const { campaign, simpleStats, templateLabels } = pickedCampaign;
    const isWinnerTemplateSimpleStats = getWinnerTemplate(simpleStats?.CampaignTemplatesSummary);
    const hasWinnerAfter3h = campaign?.SplitOptions?.DetermineWinnerAfterXMinutes > 180;
    const isNewAbSplitCampaign = isNewAbSplit(simpleStats?.CampaignTemplatesSummary);
    const { AdvancedOptions } = useSelector((state: ApplicationState) => state.account);
    const browser = useSelector((state: ApplicationState) => state.browser);
    const isMobile = browser.lessThan.hd;
    const { t } = useTranslation();
    useEffect(() => {
        dispatch(
            GetCampaignByName({
                name: decodedName,
                campaignType,
                isAbSplitClickAndOpenTestResultsRequired: true,
                isSimpleStatsRequired: true,
                isLinkTrackingAbSplitRequired: true,
            })
        );

        return () => {
            dispatch(CancelPollingCampaignSimpleStats());
        };
    }, []);

    const { loading } = useSelector((state: ApplicationState) => state.reports.linkTrackingAbSplit);

    return (
        <Box pt="32px">
            <TestConditions pickedCampaign={pickedCampaign} />
            {!isMobile && (
                <Box paddingBottom={simpleStats?.CampaignTemplatesSummary?.length > 8 ? '32px' : '0px'}>
                    <Box mb="32px" />
                    <BarChart pickedCampaign={pickedCampaign} />
                    <Box mb="68px" />
                </Box>
            )}
            {isMobile && (
                <>
                    <Box mt="18px" />
                    <MobileCampaignTable pickedCampaign={pickedCampaign} />
                    <Box mt={'64px'} />{' '}
                </>
            )}
            <TestResults simpleStats={simpleStats} />
            {!isMobile && <TestResultsTable pickedCampaign={pickedCampaign} advancedOptions={AdvancedOptions} />}
            {isMobile && !isEmpty(simpleStats) && (
                <TestResultsTableMobile pickedCampaign={pickedCampaign} advancedOptions={AdvancedOptions} />
            )}
            {isEmpty(simpleStats) && <EmptyState text={t('thereisnodatatodisplay')} />}
            {hasWinnerAfter3h && !isMobile && <AbSplitClicksAndOpensLineChart />}
            {!isMobile && (
                <RenderLinksTable
                    name={decodedName}
                    type={campaignType}
                    links={links}
                    templateLabels={templateLabels}
                    isNewAbSplitCampaign={isNewAbSplitCampaign}
                />
            )}
            {isMobile && !isEmpty(links) && (
                <LinksTableMobile
                    name={decodedName}
                    type={campaignType}
                    links={links}
                    templateLabels={templateLabels}
                    isNewAbSplitCampaign={isNewAbSplitCampaign}
                />
            )}
            {isMobile && isEmpty(links) && !loading && (
                <Box mt="32px">
                    <Typography variant="h3">{t('links')}</Typography>
                    <Paper padding={{ top: 24, bottom: 32 }} margin={{ top: 32 }}>
                        <EmptyState text={t('thereisnodatatodisplay')} />
                    </Paper>
                </Box>
            )}
            {isNewAbSplitCampaign && !isEmpty(isWinnerTemplateSimpleStats) && !isMobile && (
                <GetWinnerResults pickedCampaign={pickedCampaign} advancedOptions={AdvancedOptions} />
            )}
            {isNewAbSplitCampaign && !isEmpty(isWinnerTemplateSimpleStats) && isMobile && (
                <WinnerResultsMobile pickedCampaign={pickedCampaign} advancedOptions={AdvancedOptions} />
            )}
        </Box>
    );
};

export default CampaignReportAbSplit;
